/**
 * The account menu — a native `<details>` disclosure, and deliberately NOT a
 * focus trap.
 *
 * A trap belongs to a surface that suspends the page until it is answered, and
 * this menu suspends nothing; trapping `Tab` inside it would break the reader's
 * expectation rather than serve it. What IS added on top of the native
 * behaviour is `Esc` closing with focus returned to the summary, and a click
 * outside closing it.
 *
 * The panel is anchored on `inset-inline-end` and grows toward the reading
 * start. Anchoring `inset-inline-start` sends it off-screen under `rtl`, which
 * is the exact trap the design system flags for logical offsets.
 *
 * There is no admin entry point here — no role badge and no link into the
 * console. An operator who also subscribes reaches it by typing its host and
 * signing in there: the cookie is host-only, so the two sessions never meet.
 *
 * The avatar is a neutral GRADIENT, never an initial. An Arabic letter's shape
 * changes with joining, so a monogram is ambiguous exactly where it is meant
 * to identify.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { apiClient } from '@shared/api/client';
import { clearSession } from '@shared/auth/session';
import { useSessionProfile } from '@shared/auth/useSession';
import { ConfirmDialog } from '@shared/ui';

import { chromeCopy } from './copy';
import { IconBookmark, IconProfile, IconSettings, IconSignOut, IconSubscription, IconSupport } from './icons';
import { accountEntries, type AccountEntryId } from './nav';

const AVATAR_CLASS =
  'block h-6 w-6 shrink-0 rounded-pill border border-border ' +
  'bg-[linear-gradient(135deg,color-mix(in_srgb,var(--brand-noir)_78%,var(--brand-bone)_22%),color-mix(in_srgb,var(--brand-noir)_92%,var(--brand-bone)_8%))]';

const ENTRY_CLASS =
  'flex min-h-11 items-center gap-3 rounded-sm px-3 text-sm text-ink-muted no-underline transition-colors duration-fast hover:bg-hover-bg hover:text-accent-text ' +
  'aria-[current=page]:bg-accent-soft aria-[current=page]:font-semibold aria-[current=page]:text-accent-text';

const ENTRY_ICON: Readonly<Record<AccountEntryId, (props: { className?: string }) => ReactNode>> = {
  profile: IconProfile,
  lists: IconBookmark,
  subscription: IconSubscription,
  support: IconSupport,
  settings: IconSettings,
};

export function AccountMenu() {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const summaryRef = useRef<HTMLElement | null>(null);
  const profile = useSessionProfile();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent): void => {
      if (!detailsRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      summaryRef.current?.focus();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  /**
   * Sign-out is an ACTION, not a destination, so it is a button rather than a
   * link to `/login`. The revoke is best-effort and the local clear is not: a
   * refusal from the server must never leave the reader holding a session the
   * interface has already told them is over.
   */
  const signOut = async (): Promise<void> => {
    setSigningOut(true);
    try {
      await apiClient.post('/sessions:revoke', undefined, { skipAuthRefresh: true });
    } catch {
      /* the session is ending either way */
    }
    clearSession();
    setConfirming(false);
    setSigningOut(false);
    // BARE `/login`, WITH NO `next` — deliberate, and the one place in this app
    // where the parameter is withheld rather than forgotten.
    //
    // `next` carries an INTERRUPTED intent: the reader asked for a page, the
    // guard sent them to sign in, and the parameter is what returns them to the
    // page they asked for. Signing out is the opposite — a completed intent —
    // and there is nothing to return to. Carrying the page they were on would
    // aim the next sign-in at whatever surface they happened to be leaving from,
    // which is not a destination anyone chose, and on a shared device it would
    // name the last thing the previous reader was watching on the screen the
    // next one signs in on.
    navigate('/login', { replace: true });
  };

  return (
    <>
      <details
        ref={detailsRef}
        open={open}
        className="relative shrink-0"
        onToggle={(event) => setOpen(event.currentTarget.open)}
      >
        <summary
          ref={(node) => {
            summaryRef.current = node;
          }}
          aria-label={
            profile ? chromeCopy.account.menuFor(profile.displayName) : chromeCopy.account.menuForAnonymous
          }
          className="flex min-h-11 w-11 cursor-pointer list-none items-center justify-center rounded-sm open:bg-active-bg hover:bg-hover-bg [&::-webkit-details-marker]:hidden"
        >
          {profile?.avatarUrl ? (
            <img
              src={profile.avatarUrl}
              alt=""
              width={32}
              height={32}
              className={`${AVATAR_CLASS} object-cover`}
            />
          ) : (
            <span aria-hidden="true" className={AVATAR_CLASS} />
          )}
        </summary>

        <nav
          aria-label={chromeCopy.accountMenuLabel}
          className="absolute end-0 top-[calc(100%+var(--space-2))] z-[60] w-52 rounded-md border border-border bg-surface-overlay p-2 shadow-elev-3"
        >
          <ul className="m-0 list-none p-0">
            {accountEntries.map((entry) => {
              const Icon = ENTRY_ICON[entry.id];
              return (
                <li key={entry.id}>
                  <Link
                    to={entry.href}
                    // The attribute marks the entry that actually points at this
                    // page, and nothing else claims to be it.
                    aria-current={location.pathname === entry.href ? 'page' : undefined}
                    className={ENTRY_CLASS}
                    onClick={() => setOpen(false)}
                  >
                    <Icon className="shrink-0" />
                    <span>{entry.label}</span>
                  </Link>
                </li>
              );
            })}
            <li>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setConfirming(true);
                }}
                className={`${ENTRY_CLASS} w-full text-start`}
              >
                <IconSignOut className="shrink-0" />
                <span>{chromeCopy.account.signOut}</span>
              </button>
            </li>
          </ul>
        </nav>
      </details>

      {/*
        Sign-out is CONFIRMED, and the confirmation is `warning` rather than
        `danger`: nothing is destroyed by it. The account, the list and the
        subscription all survive, and the reader signs back in — `--danger` is
        reserved for the destruction it names.

        The dialog is a SIBLING of the disclosure rather than a child of it, so
        the menu's own outside-pointer handler never sees the dialog's clicks.
        The `<dialog>` itself is in the browser's top layer, so nesting would
        have rendered correctly either way; the sibling placement is about the
        handler, not about the paint.
      */}
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => void signOut()}
        intent="warning"
        busy={signingOut}
        title={chromeCopy.account.signOutConfirm.title}
        description={chromeCopy.account.signOutConfirm.body}
        confirmLabel={chromeCopy.account.signOutConfirm.confirm}
        cancelLabel={chromeCopy.account.signOutConfirm.cancel}
      />
    </>
  );
}
